import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import RemediationModal from './ai/RemediationModal';
import { authUrl } from '../utils/api';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

function getPackageEcosystem(purl) {
  if (!purl) return 'unknown';
  const match = purl.match(/^pkg:([^/]+)\//);
  return match ? match[1] : 'unknown';
}

function isOSPackage(purl) {
  return ['apk', 'deb', 'rpm', 'oci'].includes(getPackageEcosystem(purl));
}

function getDistroFromPURL(purl) {
  const match = purl && purl.match(/[?&]distro=([^&]+)/);
  if (!match) return null;
  return match[1]
    .replace(/^alpine-/, 'Alpine ')
    .replace(/^debian-/, 'Debian ')
    .replace(/^ubuntu-/, 'Ubuntu ');
}

function scorecardColor(score) {
  if (score == null || score < 0) return 'secondary';
  if (score >= 8) return 'success';
  if (score >= 5) return 'warning';
  return 'danger';
}

function ageLabel(days) {
  if (days < 0) return null;
  if (days < 30) return { label: `${days}d ago`, color: 'success' };
  if (days < 365) return { label: `${Math.round(days / 30)}mo ago`, color: 'success' };
  const years = (days / 365).toFixed(1);
  return { label: `${years}y ago`, color: days > 730 ? 'danger' : 'warning' };
}

function EcosystemBadge({ ecosystem }) {
  return <span className="ce-tag" style={{ fontSize: '0.65rem' }}>{ecosystem || '?'}</span>;
}

function SummaryCard({ label, value, color, icon }) {
  return (
    <div className="ce-stat-card" style={{ '--stat-color': `var(--color-${color})` }}>
      <div style={{ fontSize: '1rem', marginBottom: '0.125rem' }}>{icon}</div>
      <div className="ce-stat-value">{value}</div>
      <div className="ce-stat-label">{label}</div>
    </div>
  );
}

function ageInfo(days) {
  if (days == null || days < 0) return null;
  if (days < 180) return { label: days < 30 ? `${days}d` : `${Math.round(days / 30)}mo`, color: 'success' };
  if (days < 540) return { label: `${Math.floor(days / 365)}y ${Math.round((days % 365) / 30)}mo`, color: 'warning' };
  return { label: `${(days / 365).toFixed(1)}y`, color: 'danger' };
}

function RiskScoreBadge({ score }) {
  const { t } = useTranslation();
  if (score == null) return null;
  let hex, colorName, label;
  if (score >= 81) { hex = 'var(--color-success)'; colorName = 'success'; label = t('health.lowRisk', 'Low Risk'); }
  else if (score >= 61) { hex = 'var(--color-warning)'; colorName = 'warning'; label = t('health.mediumRisk', 'Medium Risk'); }
  else if (score >= 31) { hex = 'var(--color-warning)'; colorName = 'warning'; label = t('health.highRisk', 'High Risk'); }
  else { hex = 'var(--color-danger)'; colorName = 'danger'; label = t('health.criticalRisk', 'Critical Risk'); }
  return (
    <div className="ce-stat-card" style={{ '--stat-color': hex, minWidth: 140 }} title="Supply Chain Risk Score: 0=worst, 100=best">
      <div className="ce-stat-value">{score}</div>
      <div className="ce-stat-label">{t('health.supplyChainRisk', 'Supply Chain Risk')}</div>
      <span className={`badge bg-${colorName} mt-1`}>{label}</span>
    </div>
  );
}

function anomalyBadgeStyle(severity) {
  switch (severity) {
    case 'HIGH':
      return { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' };
    case 'MEDIUM':
      return { background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' };
    case 'LOW':
      return { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' };
    default:
      return { background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' };
  }
}

// ── Community Health Panel ────────────────────────────────────────────────────

function gradeColor(grade) {
  switch (grade) {
    case 'A': return '#22c55e';
    case 'B': return '#84cc16';
    case 'C': return '#eab308';
    case 'D': return '#f97316';
    case 'F': return '#ef4444';
    default: return '#6b7280';
  }
}

function MetricRow({ label, icon, score, value, unit }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 75 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: 13, width: 18, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontSize: 12, color: '#c9d1d9', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
          <span style={{ fontSize: 11, color: '#8b949e', whiteSpace: 'nowrap', marginLeft: 4 }}>{value !== undefined && value !== null ? `${value}${unit || ''}` : '—'}</span>
        </div>
        <div style={{ height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.5s ease' }} />
        </div>
      </div>
      <span style={{ fontSize: 10, color, fontWeight: 700, width: 26, textAlign: 'right', flexShrink: 0 }}>{pct}</span>
    </div>
  );
}

function CategoryCard({ title, icon, metrics }) {
  const scores = metrics.map(m => m.score || 0).filter(s => s > 0);
  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const borderColor = avg >= 75 ? '#22c55e' : avg >= 50 ? '#eab308' : '#ef4444';
  return (
    <div style={{ borderLeft: `3px solid ${borderColor}`, background: 'rgba(255,255,255,0.02)', borderRadius: '0 8px 8px 0', padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{icon} {title}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: borderColor, background: `${borderColor}18`, border: `1px solid ${borderColor}44`, borderRadius: 100, padding: '1px 6px' }}>{avg}/100</span>
      </div>
      {metrics.map(m => <MetricRow key={m.label} {...m} />)}
    </div>
  );
}

function CommunityHealthPanel({ health, loading }) {
  if (loading) {
    return (
      <div style={{ padding: '16px 0 0' }}>
        <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '2px solid #6b7280', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          Loading community health…
        </div>
      </div>
    );
  }
  if (!health) return null;
  if (health.dataQuality === 'unavailable') {
    return (
      <div style={{ padding: '12px 0 0' }}>
        <div style={{ fontSize: 11, color: '#6b7280' }}>🏘️ No GitHub data available for this package</div>
      </div>
    );
  }
  const gc = gradeColor(health.grade);

  const peopleMetrics = [
    { label: 'Bus Factor', icon: '🚌', score: health.busFactorScore, value: health.busFactor >= 0 ? health.busFactor : undefined, unit: ' contributors' },
    { label: 'Contributor Retention', icon: '👥', score: health.retentionScore, value: health.retentionRate >= 0 ? Math.round(health.retentionRate * 100) : undefined, unit: '%' },
    { label: 'Growth Trend', icon: '📈', score: health.growthScore, value: health.commitTrend !== undefined ? (health.commitTrend >= 0 ? '+' : '') + Math.round(health.commitTrend * 100) : undefined, unit: '% vs prev qtr' },
  ];
  const responsivenessMetrics = [
    { label: 'Bug Management (BMI)', icon: '🐛', score: health.bmiScore, value: health.bmi >= 0 ? health.bmi.toFixed(2) : undefined, unit: '× close rate' },
    { label: 'Review Efficiency (REI)', icon: '👁️', score: health.reiScore, value: health.rei >= 0 ? health.rei.toFixed(2) : undefined, unit: '× merge rate' },
    { label: 'Response Time', icon: '💬', score: health.responseScore, value: health.medianResponseDays >= 0 ? Math.round(health.medianResponseDays) : undefined, unit: 'd median' },
  ];
  const velocityMetrics = [
    { label: 'Lead Time (PR → merge)', icon: '⏱️', score: health.leadTimeScore, value: health.medianMergeDays >= 0 ? Math.round(health.medianMergeDays) : undefined, unit: 'd median' },
  ];

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: `${gc}18`, border: `2px solid ${gc}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: gc, flexShrink: 0 }}>
          {health.grade || '?'}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#e6edf3' }}>Community Health</div>
          <div style={{ fontSize: 10, color: '#8b949e' }}>{health.compositeScore}/100 · {health.gitHubPath}</div>
        </div>
        {health.dataQuality === 'partial' && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#eab308', background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: 100, padding: '1px 6px', flexShrink: 0 }}>partial data</span>
        )}
      </div>
      {/* 3 category cards */}
      <CategoryCard title="People & Sustainability" icon="👥" metrics={peopleMetrics} />
      <CategoryCard title="Responsiveness" icon="⚡" metrics={responsivenessMetrics} />
      <CategoryCard title="Development Velocity" icon="⏱️" metrics={velocityMetrics} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function PackageHealthView({ repoName, sbomId, onHealthData, initialData }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState('');

  // Derive all filter state from URL
  const search = searchParams.get('health_search') || '';
  const page = Math.max(1, parseInt(searchParams.get('health_page') || '1', 10));
  const pageSize = parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10);

  const activeFlags = (searchParams.get('health_flags') || '').split(',').filter(Boolean);
  const filterOutdated    = activeFlags.includes('outdated');
  const filterAdvisories  = activeFlags.includes('advisories');
  const filterDeprecated  = activeFlags.includes('deprecated');
  const filterMalicious   = activeFlags.includes('malicious');
  const filterAbandoned   = activeFlags.includes('abandoned');
  const filterUnpinned    = activeFlags.includes('unpinned');
  const filterAgeOutdated = activeFlags.includes('age');
  const filterAnomalous   = activeFlags.includes('anomalous');

  const [expandedScorecard, setExpandedScorecard] = useState(null);
  const [crossRepoPackage, setCrossRepoPackage] = useState(null);
  const [crossRepoData, setCrossRepoData] = useState(null);
  const [crossRepoLoading, setCrossRepoLoading] = useState(false);
  const [remediationPkg, setRemediationPkg] = useState(null);
  const [communityHealth, setCommunityHealth] = useState({});
  const [chLoading, setChLoading] = useState({});

  const setSearch = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('health_search', val); n.set('health_page', '1'); return n; });
  const setPage   = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('health_page', String(val)); return n; });

  const toggleFlag = (flag) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      const flags = (n.get('health_flags') || '').split(',').filter(Boolean);
      const next = flags.includes(flag) ? flags.filter(f => f !== flag) : [...flags, flag];
      n.set('health_flags', next.join(','));
      n.set('health_page', '1');
      return n;
    });
  };

  useEffect(() => {
    if (initialData !== undefined) return; // parent controls
    if (!repoName || !sbomId) return;
    setLoading(true);
    setError('');
    setData(null);
    axios.get(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/health`)
      .then(r => { setData(r.data); if (onHealthData) onHealthData(r.data); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [repoName, sbomId, onHealthData]);

  useEffect(() => {
    if (initialData === undefined) return;
    setData(initialData || null);
    setLoading(false);
    if (initialData && onHealthData) onHealthData(initialData);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCrossRepoLookup = (p) => {
    setCrossRepoPackage({ name: p.name, purl: p.purl });
    setCrossRepoData(null);
    setCrossRepoLoading(true);
    axios.get(`${config.BASE_URL}/packages/repos`, { params: { purl: p.purl } })
      .then(r => setCrossRepoData(r.data))
      .catch(() => setCrossRepoData([]))
      .finally(() => setCrossRepoLoading(false));
  };

  const fetchCommunityHealth = async (purl, projectUrl, key) => {
    setChLoading(prev => ({ ...prev, [key]: true }));
    setCommunityHealth(prev => ({ ...prev, [key]: null }));
    try {
      const params = new URLSearchParams();
      if (purl) params.set('purl', purl);
      if (projectUrl) {
        const match = projectUrl.match(/github\.com\/[^/]+\/[^/?#]+/);
        if (match) params.set('github_path', match[0]);
      }
      const res = await axios.get(authUrl(`${config.BASE_URL}/packages/community-health?${params}`));
      setCommunityHealth(prev => ({ ...prev, [key]: res.data }));
    } catch (e) {
      setCommunityHealth(prev => ({ ...prev, [key]: { dataQuality: 'unavailable', error: e.message } }));
    } finally {
      setChLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const filtered = data?.packages?.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterOutdated && !p.isOutdated) return false;
    if (filterAdvisories && p.advisoryCount === 0) return false;
    if (filterDeprecated && !p.isDeprecated) return false;
    if (filterMalicious && !p.isMalicious) return false;
    if (filterAbandoned && !p.isAbandoned) return false;
    if (filterUnpinned && p.isPinned) return false;
    if (filterAgeOutdated && !(p.ageInDays > 540)) return false;
    if (filterAnomalous && !p.isMaintenanceAnomalous) return false;
    return true;
  }) || [];

  const nPages = Math.ceil(filtered.length / pageSize);
  const pagedFiltered = filtered.slice((page - 1) * pageSize, page * pageSize);

  const allOSPackages = filtered.length > 0 && filtered.every(p => isOSPackage(p.purl));


  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Table rows={6} cols={8} />
      <div className="small text-secondary mt-3">{t('health.loadingAnalysis', 'Analysing packages... this may take 15-30 seconds')}</div>
    </div>
  );

  if (error) return (
    <div className="alert alert-danger">{error}</div>
  );

  if (!data) return null;

  return (
    <div>
      {/* Header row with Risk Score and Export */}
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <RiskScoreBadge score={data.summary?.riskScore} />
        <button
          className="ce-btn ce-btn--secondary ce-btn--sm"
          onClick={() => window.open(authUrl(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/export/health`))}
          title="Export health data as CSV"
        >
          📥 {t('health.exportCsv')}
        </button>
      </div>

      {/* Summary cards */}
      {allOSPackages && (
        <div className="iac-health-notice">
          <span>🐳 Container SBOM detected.</span>
          <span>OS-level packages (apk/deb/rpm) are not tracked by deps.dev. Vulnerability coverage is provided by OSV — see the Vulnerabilities tab.</span>
        </div>
      )}
      <div className="ce-stats-grid">
        <SummaryCard label={t('health.totalPackages')} value={data.totalPackages} color="primary" icon="📦" />
        <SummaryCard label={t('health.outdated')} value={data.outdatedCount} color={data.outdatedCount > 0 ? 'warning' : 'success'} icon="⏰" />
        <SummaryCard label={t('health.withAdvisories')} value={data.withAdvisories} color={data.withAdvisories > 0 ? 'danger' : 'success'} icon="🔒" />
        {data.maliciousCount > 0 && (
          <SummaryCard label={t('health.malicious')} value={data.maliciousCount} color="danger" icon="☠️" />
        )}
        {data.abandonedCount > 0 && (
          <SummaryCard label={t('health.abandoned')} value={data.abandonedCount} color="warning" icon="👻" />
        )}
        {data.unpinnedCount > 0 && (
          <SummaryCard label={t('health.unpinned', 'Unpinned')} value={data.unpinnedCount} color="secondary" icon="📌" />
        )}
        {data.anomalousCount > 0 && (
          <SummaryCard label={t('health.maintainerAnomalies', 'Maintainer Anomalies')} value={data.anomalousCount} color="warning" icon="⚠️" />
        )}
        <SummaryCard label={t('health.direct', 'Direct')} value={data.directCount || '?'} color="info" icon="➡️" />
        <SummaryCard label={t('health.transitive', 'Transitive')} value={data.transitiveCount || '?'} color="secondary" icon="↪️" />
        {data.avgScorecardScore >= 0 && (
          <SummaryCard label={t('health.avgScore')} value={data.avgScorecardScore.toFixed(1)} color={scorecardColor(data.avgScorecardScore)} icon="🏆" />
        )}
      </div>

      {/* Filters */}
      <div className="ce-filter-bar">
        <input
          type="text"
          className="ce-search-input"
          placeholder={t('health.searchPlaceholder', 'Search packages…')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {[
          { key: 'filterOutdated', label: t('health.filterOutdated', 'Outdated'), flag: 'outdated' },
          { key: 'filterAdvisories', label: t('health.filterAdvisories', 'With advisories'), flag: 'advisories' },
          { key: 'filterDeprecated', label: t('health.filterDeprecated', 'Deprecated'), flag: 'deprecated' },
          { key: 'filterMalicious', label: t('health.filterMalicious', '☠️ Malicious'), flag: 'malicious' },
          { key: 'filterAbandoned', label: t('health.filterAbandoned', '👻 Abandoned'), flag: 'abandoned' },
          { key: 'filterUnpinned', label: t('health.filterUnpinned', '📌 Unpinned'), flag: 'unpinned' },
          { key: 'filterAgeOutdated', label: `🕰️ ${t('health.filterOld')}`, flag: 'age' },
          { key: 'filterAnomalous', label: t('health.filterAnomalous', '⚠️ Anomalous'), flag: 'anomalous' },
        ].map(({ key, label, flag }) => (
          <button
            key={key}
            className={`ce-btn ce-btn--sm ${activeFlags.includes(flag) ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
            onClick={() => toggleFlag(flag)}
          >
            {label}
          </button>
        ))}
        <span className="ce-filter-count">{filtered.length} {t('health.packagesCount', 'packages')}</span>
      </div>

      {/* Table */}
      <div className="ce-table-wrap">
        <table className="ce-table ce-table--clickable">
          <thead>
            <tr>
              <th>{t('health.columns.name')}</th>
              <th>{t('health.columns.version')}</th>
              <th>{t('health.columnLatest', 'Latest')}</th>
              <th>{t('health.columnPublished', 'Published')}</th>
              <th>{t('health.columns.age')}</th>
              <th>{t('health.columns.advisories')}</th>
              <th>{t('health.columns.license')}</th>
              <th>{t('health.columns.scorecard')}</th>
              <th>{t('health.columnStars', 'Stars')}</th>
              <th>{t('health.columnFlags', 'Flags')}</th>
            </tr>
          </thead>
          <tbody>
            {pagedFiltered.map(p => (
              <React.Fragment key={p.purl || p.name}>
                <tr
                  className={p.isMalicious ? 'table-danger' : p.isAbandoned ? 'table-warning' : p.isMaintenanceAnomalous ? 'table-warning' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const key = p.purl || p.name;
                    const isExpanding = expandedScorecard !== key;
                    setExpandedScorecard(isExpanding ? key : null);
                    if (isExpanding && !communityHealth[key]) {
                      fetchCommunityHealth(p.purl, p.projectUrl, key);
                    }
                  }}
                >
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <EcosystemBadge ecosystem={p.ecosystem} />
                      {p.depsDevUrl
                        ? <a href={p.depsDevUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-semibold" onClick={e => e.stopPropagation()}>{p.name}</a>
                        : <span className="fw-semibold">{p.name}</span>
                      }
                      <button className="ce-btn ce-btn--secondary ce-btn--sm" title="Find in other repos" onClick={e => { e.stopPropagation(); handleCrossRepoLookup(p); }}>🔍</button>
                      <button className="ce-btn ce-btn--secondary ce-btn--sm" title="AI Remediation" onClick={e => { e.stopPropagation(); setRemediationPkg(p); }}>💡</button>
                      {p.isTransitive && <span className="ce-tag" style={{ fontSize: '0.6rem' }}>transitive</span>}
                    </div>
                    {isOSPackage(p.purl) && (
                      <div className="iac-os-pkg-badge mt-1">
                        <span>🐧 OS Package</span>
                        {getDistroFromPURL(p.purl) && <span className="iac-os-pkg-distro">{getDistroFromPURL(p.purl)}</span>}
                        <span className="iac-os-pkg-note">Health tracked via OSV vulnerability database only</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <code className="small">{p.currentVersion || '—'}</code>
                    {!p.isPinned && p.currentVersion && <span title="Floating version" className="ms-1">📌</span>}
                  </td>
                  <td>
                    {p.latestVersion
                      ? <span className={p.isOutdated ? 'text-warning fw-semibold' : 'text-success'}>{p.latestVersion}</span>
                      : <span className="text-secondary">—</span>}
                  </td>
                  <td>
                    {(() => { const a = ageLabel(p.daysSinceLatest); return a ? <span className={`text-${a.color}`}>{a.label}</span> : <span className="text-secondary">—</span>; })()}
                  </td>
                  <td>
                    {(() => { const a = ageInfo(p.ageInDays); return a ? <span className={`text-${a.color} small`} title={`${p.ageInDays} days since current version published`}>{a.label}</span> : <span className="text-secondary small">—</span>; })()}
                  </td>
                  <td>
                    {p.advisoryCount > 0
                      ? <span className="ce-sev ce-sev--critical">{p.advisoryCount} CVE{p.advisoryCount > 1 ? 's' : ''}</span>
                      : <span className="text-success small">✓</span>}
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                      {(p.licenses || []).slice(0, 2).map(l => <span key={l} className="ce-tag">{l}</span>)}
                      {(p.licenses || []).length > 2 && <span className="ce-tag">+{p.licenses.length - 2}</span>}
                      {(!p.licenses || p.licenses.length === 0) && <span className="text-secondary small">—</span>}
                      {p.licenseRisk && p.licenseRisk !== 'UNKNOWN' && (
                        <span title={p.licenseRisk.replace(/_/g, ' ')} style={{
                          fontSize: '0.6rem', padding: '1px 5px', borderRadius: 4, fontWeight: 600,
                          background: p.licenseRisk === 'STRONG_COPYLEFT' ? '#fef2f2' : p.licenseRisk === 'WEAK_COPYLEFT' ? '#fffbeb' : '#f0fdf4',
                          color: p.licenseRisk === 'STRONG_COPYLEFT' ? '#dc2626' : p.licenseRisk === 'WEAK_COPYLEFT' ? '#d97706' : '#16a34a',
                          border: `1px solid ${p.licenseRisk === 'STRONG_COPYLEFT' ? '#fca5a5' : p.licenseRisk === 'WEAK_COPYLEFT' ? '#fde68a' : '#bbf7d0'}`,
                        }}>
                          {p.licenseRisk === 'STRONG_COPYLEFT' ? '🔴' : p.licenseRisk === 'WEAK_COPYLEFT' ? '🟡' : '🟢'}
                        </span>
                      )}
                      {p.licenseRisk === 'UNKNOWN' && (!p.licenses || p.licenses.length === 0) && (
                        <span title="License unknown" style={{ fontSize: '0.6rem', padding: '1px 5px', borderRadius: 4, background: '#f8f9fa', color: '#6c757d', border: '1px solid #dee2e6', fontWeight: 600 }}>⚪</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {p.scorecard
                      ? (
                        <>
                          <div className="ce-score-bar">
                            <div className={`ce-score-bar-fill ${p.scorecard.score >= 8 ? 'ce-score-bar-fill--green' : p.scorecard.score >= 5 ? 'ce-score-bar-fill--yellow' : 'ce-score-bar-fill--red'}`}
                              style={{ width: `${(p.scorecard.score / 10) * 100}%` }} />
                          </div>
                          <span style={{ fontSize: '0.75rem' }}>{p.scorecard.score.toFixed(1)}</span>
                        </>
                      )
                      : <span className="text-secondary small">N/A</span>}
                    {p.maintenanceScore >= 0 && (
                      <div className="small text-secondary mt-1">Maintained {p.maintenanceScore}/10</div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column">
                      {p.stars > 0
                        ? <span className="text-secondary small">⭐ {p.stars >= 1000 ? (p.stars / 1000).toFixed(1) + 'k' : p.stars}</span>
                        : <span className="text-secondary small">—</span>}
                      {p.contributorCount > 0 && <span className="text-secondary small">👥 {p.contributorCount}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      {p.isMalicious && <span title="Malicious package!" className="ce-sev ce-sev--critical">☠️ MALICIOUS</span>}
                      {p.isAbandoned && <span title={p.abandonedReason || 'Abandoned project'} className="ce-sev ce-sev--critical">👻 {p.isArchived ? 'Archived' : 'Abandoned'}</span>}
                      {p.isDeprecated && <span title="Deprecated" className="ce-sev ce-sev--critical">Deprecated</span>}
                      {p.isMaintenanceAnomalous && (
                        <span
                          title={p.anomalySummary || 'Maintainer anomaly detected'}
                          style={{ ...anomalyBadgeStyle(p.anomalySeverity), borderRadius: 4, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700 }}
                        >
                          ⚠️ {p.anomalySeverity || 'ANOMALY'} {p.anomalyScore ? `(${p.anomalyScore})` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedScorecard === (p.purl || p.name) && (
                   <tr className="ce-expand-panel">
                     <td colSpan={10} className="px-4 py-3">
                       {/* ── Summary bar ───────────────────────────────────── */}
                       <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                         {p.scorecard && (
                           <span style={{ fontSize: 12, fontWeight: 700, background: p.scorecard.score >= 8 ? '#16a34a22' : p.scorecard.score >= 5 ? '#d9770622' : '#dc262622', color: p.scorecard.score >= 8 ? '#16a34a' : p.scorecard.score >= 5 ? '#d97706' : '#dc2626', border: `1px solid ${p.scorecard.score >= 8 ? '#16a34a44' : p.scorecard.score >= 5 ? '#d9770644' : '#dc262644'}`, borderRadius: 6, padding: '3px 10px' }}>
                             OpenSSF {p.scorecard.score.toFixed(1)}/10
                           </span>
                         )}
                         {communityHealth[p.purl || p.name] && communityHealth[p.purl || p.name].grade && (
                           <span style={{ fontSize: 12, fontWeight: 700, background: `${gradeColor(communityHealth[p.purl || p.name].grade)}18`, color: gradeColor(communityHealth[p.purl || p.name].grade), border: `1px solid ${gradeColor(communityHealth[p.purl || p.name].grade)}44`, borderRadius: 6, padding: '3px 10px' }}>
                             Community {communityHealth[p.purl || p.name].grade} — {communityHealth[p.purl || p.name].compositeScore}/100
                           </span>
                         )}
                         {p.isMaintenanceAnomalous && (
                           <span style={{ ...anomalyBadgeStyle(p.anomalySeverity), borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                             ⚠️ Anomaly: {p.anomalySeverity || 'LOW'}
                           </span>
                         )}
                         {p.isAbandoned && <span style={{ fontSize: 12, fontWeight: 700, background: '#dc262618', color: '#dc2626', border: '1px solid #dc262644', borderRadius: 6, padding: '3px 10px' }}>👻 {p.isArchived ? 'Archived' : 'Abandoned'}</span>}
                         {p.projectUrl && <a href={p.projectUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#8b949e', marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>{p.projectUrl}</a>}
                       </div>

                       {/* ── Two-column: Scorecard left / Community Health right ── */}
                       <div style={{ display: 'grid', gridTemplateColumns: p.scorecard ? '1fr 340px' : '1fr', gap: 20, alignItems: 'start' }}>
                         {/* Left: OpenSSF Scorecard checks */}
                         {p.scorecard && (
                           <div>
                             <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>OpenSSF Scorecard checks</div>
                             <div className="row g-2">
                               {(p.scorecard.checks || []).map(c => (
                                 <div key={c.name} className="col-md-6 col-lg-4">
                                   <div className="d-flex align-items-start gap-2 p-2 bg-white rounded border">
                                     <span className={`badge bg-${scorecardColor(c.score)} mt-1`} style={{ minWidth: 32 }}>
                                       {c.score >= 0 ? c.score : '?'}
                                     </span>
                                     <div>
                                       <div className="small fw-semibold">{c.name}</div>
                                       <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{c.reason}</div>
                                     </div>
                                   </div>
                                 </div>
                               ))}
                             </div>
                           </div>
                         )}

                         {/* Right: Community Health (categorized) */}
                         <div style={{ borderLeft: p.scorecard ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingLeft: p.scorecard ? 20 : 0 }}>
                           <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Community Health</div>
                           <CommunityHealthPanel
                             health={communityHealth[p.purl || p.name]}
                             loading={!!chLoading[p.purl || p.name]}
                           />
                         </div>
                       </div>

                       {/* ── Anomaly signals (below, only if present) ─────── */}
                       {p.isMaintenanceAnomalous && (
                         <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: '#fffaf0', border: '1px solid #fde68a' }}>
                           <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                             <strong style={{ fontSize: 12 }}>Maintainer anomaly signals</strong>
                             {p.contributorCount > 0 && <span className="small text-secondary">Contributors: {p.contributorCount}</span>}
                             {p.versionLag > 0 && <span className="small text-secondary">Version lag: {p.versionLag}</span>}
                           </div>
                           {p.anomalySummary && <div className="small mb-1">{p.anomalySummary}</div>}
                           {(p.anomalySignals || []).length > 0 && (
                             <ul className="small mb-0 ps-3">
                               {p.anomalySignals.map((signal) => <li key={signal}>{signal}</li>)}
                             </ul>
                           )}
                         </div>
                       )}
                     </td>
                   </tr>
                 )}
              </React.Fragment>
            ))}
            {pagedFiltered.length === 0 && (
              <tr>
                <td colSpan={10} className="py-4">
                  <EmptyState
                    icon="📦"
                    title={t('health.emptyFilteredTitle')}
                    description={t('health.emptyFilteredDescription')}
                    size="sm"
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {nPages > 1 && (
        <Pagination
          nPages={nPages}
          currentPage={page}
          setCurrentPage={setPage}
          pageSize={pageSize}
          totalItems={filtered.length}
        />
      )}

      {/* Cross-repo lookup modal */}
      {crossRepoPackage && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setCrossRepoPackage(null)}>
          <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔍 Repos using <code>{crossRepoPackage.name}</code></h5>
                <button className="btn-close" onClick={() => setCrossRepoPackage(null)} />
              </div>
              <div className="modal-body">
                {crossRepoLoading && (
                  <div className="py-2">
                    <Skeleton.Text lines={2} lastWidth="45%" />
                  </div>
                )}
                {!crossRepoLoading && crossRepoData && crossRepoData.length === 0 && (
                  <EmptyState
                    icon="🔍"
                    title={t('health.crossRepoEmptyTitle')}
                    description={t('health.crossRepoEmptyDescription')}
                    size="sm"
                  />
                )}
                {!crossRepoLoading && crossRepoData && crossRepoData.length > 0 && (
                  <div>
                    <p className="text-secondary small mb-2">Found in <strong>{crossRepoData.length}</strong> repository/commit combination(s):</p>
                    <div className="table-responsive">
                      <table className="ce-table">
                        <thead><tr><th>Repository</th><th>Commit</th><th>Indexed</th></tr></thead>
                        <tbody>
                          {crossRepoData.map((r, i) => (
                            <tr key={i}>
                              <td className="fw-semibold">{r.repoName}</td>
                              <td><code className="small">{r.commitHash?.substring(0, 12)}</code></td>
                              <td className="small text-secondary">{r.indexedAt?.substring(0, 10)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="ce-btn ce-btn--secondary" onClick={() => setCrossRepoPackage(null)}>{t('health.close', 'Close')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Remediation modal */}
      {remediationPkg && (
        <RemediationModal
          packageName={remediationPkg.name}
          currentVersion={remediationPkg.currentVersion}
          ecosystem={remediationPkg.ecosystem}
          cves={(remediationPkg.advisories || []).map(a => a.id || a)}
          isAbandoned={remediationPkg.isAbandoned}
          isDeprecated={remediationPkg.isDeprecated}
          onClose={() => setRemediationPkg(null)}
        />
      )}
    </div>
  );
}

export default PackageHealthView;
